import { GameState } from '../core/GameState.js';
import { DebugLog } from '../core/DebugLog.js';
import achievementsData from '../data/achievements.json';

/**
 * Tracks and awards achievements based on game events.
 * Achievements persist in GameState meta-progression.
 */
export class AchievementSystem {
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.pendingToasts = []; // queue of achievement names to display
  }

  /**
   * Check all achievements against current game state.
   * Call this periodically (e.g., on enemy kill, floor change, run end).
   * @param {object} context - Additional context for checking
   * @param {number} [context.combo] - Current combo count
   * @param {number} [context.dodgeCount] - Dodges this run
   * @param {boolean} [context.bossDefeated] - Was a boss just defeated
   * @param {boolean} [context.noDamageBoss] - Beat boss without damage
   */
  check(context = {}) {
    const unlocked = GameState.achievements || [];

    for (const [id, ach] of Object.entries(achievementsData)) {
      if (unlocked.includes(id)) continue;

      if (this._meetsCondition(ach.condition, context)) {
        unlocked.push(id);
        this.pendingToasts.push(ach.name);
        DebugLog.info('AchievementSystem', `Achievement unlocked: ${ach.name}`);
      }
    }

    GameState.achievements = unlocked;
  }

  _meetsCondition(condition, context) {
    switch (condition.type) {
      case 'enemiesDefeated':
        return GameState.enemiesDefeated >= condition.value;
      case 'floor':
        return GameState.floor >= condition.value;
      case 'goldCollected':
        return GameState.goldCollected >= condition.value;
      case 'level':
        return GameState.level >= condition.value;
      case 'combo':
        return (context.combo || 0) >= condition.value;
      case 'dodgeCount':
        return (context.dodgeCount || 0) >= condition.value;
      case 'bossDefeated':
        return context.bossDefeated === true;
      case 'noDamageBoss':
        return context.noDamageBoss === true;
      case 'relicsFound':
        return (GameState.relics || []).length >= condition.value;
      case 'relicsEquipped':
        return (GameState.relics || []).length >= condition.value;
      case 'totalRuns':
        return GameState.totalRuns >= condition.value;
      case 'totalEnemiesDefeated':
        return GameState.totalEnemiesDefeated >= condition.value;
      case 'cryptTokens':
        return GameState.cryptTokens >= condition.value;
      case 'timeSurvived':
        return GameState.getRunDuration() >= condition.value;
      case 'speedRun':
        return GameState.floor >= 3 && GameState.getRunDuration() <= condition.value;
      case 'classComplete':
        return GameState.selectedClass === condition.value && GameState.floor >= 2;
      case 'fullEquipment': {
        const eq = GameState.equipment || {};
        return eq.weapon && eq.body && eq.ring && eq.amulet;
      }
      default:
        return false;
    }
  }

  /**
   * Pop the next toast message (achievement name) if any.
   * @returns {string|null}
   */
  popToast() {
    return this.pendingToasts.shift() || null;
  }

  /**
   * Get all unlocked achievements with their data.
   * @returns {Array<{id: string, name: string, description: string}>}
   */
  getUnlocked() {
    const unlocked = GameState.achievements || [];
    return unlocked
      .filter(id => achievementsData[id])
      .map(id => ({ id, ...achievementsData[id] }));
  }

  /**
   * Get all achievements (unlocked and locked).
   * @returns {Array<{id: string, unlocked: boolean, name: string, description: string, category: string}>}
   */
  getAll() {
    const unlocked = new Set(GameState.achievements || []);
    return Object.entries(achievementsData).map(([id, ach]) => ({
      id,
      unlocked: unlocked.has(id),
      ...ach,
    }));
  }
}
